import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      if (data.token) localStorage.setItem("hstc_token", data.token);
      if (data.user) setUser(data.user);
      toast.success("Password updated. You're signed in.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-charcoal">
      <div className="w-full max-w-sm">
        <div className="mb-8"><Link to="/"><Logo /></Link></div>
        <div className="label-mono mb-2">Reset password</div>
        <h1 className="font-display text-3xl font-bold text-white mb-4">Choose a new password</h1>

        {!token ? (
          <div className="card-surface rounded-md p-5" data-testid="reset-no-token">
            <p className="text-sm text-white/75">This reset link is missing or invalid. Please request a new one.</p>
            <Link to="/forgot-password" className="mt-4 inline-block text-sm text-gold hover:text-gold-hover">Request a new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="label-mono">New password</Label>
              <div className="relative mt-1.5">
                <Input data-testid="reset-password-input" type={showPw ? "text" : "password"} required autoComplete="new-password"
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  className="bg-charcoal border-white/15 focus-visible:ring-gold text-white pr-10" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="reset-toggle-password-btn"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-white/40 hover:text-gold transition-colors">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="mt-1.5 text-[11px] font-mono text-white/40">At least 8 characters, with a letter and a number.</p>
            </div>
            <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
              className="w-full bg-gold text-charcoal hover:bg-gold-hover font-semibold rounded-sm">
              {loading ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
